import ConfigurationValidator from "./Core/Configurator/ConfigurationValidator";
import { ConfigFieldEnum } from "./Core/Configurator/Enums/ConfigFieldEnum";
import { EnvironmentEnum } from "./Core/Configurator/Enums/EnvironmentEnum";
import { ErrorHandlingModeEnum } from "./Core/Configurator/Enums/ErrorHandlingModeEnum";
import { IdSchemaEnum } from "./Core/Configurator/Enums/IdSchemaEnum";
import { LangEnum } from "./Core/Configurator/Enums/LangEnum";
import { LogEnum } from "./Core/Configurator/Enums/LogEnum";
import { ValidationResponseEnum } from "./Core/Configurator/Enums/ValidationResponseEnum";
import AuthProviderConfigModel from "./Core/Configurator/Models/AuthProviderConfigModel";
import ConfigValidationModel from "./Core/Configurator/Models/ConfigValidationModel";
import OddClientValidationModel from "./Core/Configurator/Models/OddClientValidationModel";
import SDKConfigurationModel from "./Core/Configurator/Models/SDKConfiguraitonModel";
import { getJwtTokenFromCookie } from "./Core/Global/Helper";
import FansUnitedSDKModel from "./Core/Global/Models/FansUnitedSDKModel";
import Activity from "./Core/Namespaces/Activity/Activity";
import BracketGame from "./Core/Namespaces/BracketGame/BracketGame";
import Challenges from "./Core/Namespaces/Challenges/Challenges";
import Discussions from "./Core/Namespaces/Discussions/Discussions";
import EventGame from "./Core/Namespaces/EventGame/EventGame";
import Fantasy from "./Core/Namespaces/Fantasy/Fantasy";
import Football from "./Core/Namespaces/Football/Football";
import Helpers from "./Core/Namespaces/Helpers/Helpers";
import IdMapping from "./Core/Namespaces/IdMapping/IdMapping";
import Lists from "./Core/Namespaces/Lists/Lists";
import Loyalty from "./Core/Namespaces/Loyalty/Loyalty";
import MatchQuiz from "./Core/Namespaces/MatchQuiz/MatchQuiz";
import MiniGames from "./Core/Namespaces/MiniGames/MiniGames";
import Odds from "./Core/Namespaces/Odds/Odds";
import Predictor from "./Core/Namespaces/Predictor/Predictor";
import PrivateLeagues from "./Core/Namespaces/PrivateLeagues/PrivateLeagues";
import Profile from "./Core/Namespaces/Profile/Profile";
import Progress from "./Core/Namespaces/Progress/Progress";
import StandingGame from "./Core/Namespaces/StandingGame/StandingGame";
import TopX from "./Core/Namespaces/TopX/TopX";
import Voting from "./Core/Namespaces/Voting/Voting";
import { getVersion } from "./Core/Version";

let sdkInstance: FansUnitedSDKModel | null = null;
let lastConfig: SDKConfigurationModel | null = null;

export function FansUnitedSDK(config: SDKConfigurationModel) {
  if (isNewInstanceRequired(config)) {
    const validation = validateFansUnitedConfiguration(config);
    if (validation.isValid) {
      sdkInstance = {
        profile: new Profile(config),
        football: new Football(config),
        predictor: new Predictor(config),
        loyalty: new Loyalty(config),
        topX: new TopX(config),
        matchQuiz: new MatchQuiz(config),
        activity: new Activity(config),
        miniGames: new MiniGames(config),
        discussions: new Discussions(config),
        privateLeagues: new PrivateLeagues(config),
        challenges: new Challenges(config),
        progress: new Progress(),
        voting: new Voting(config),
        fantasy: new Fantasy(config),
        helpers: new Helpers(config),
        idMapping: new IdMapping(config),
        bracketGame: new BracketGame(config),
        lists: new Lists(config),
        standingGame: new StandingGame(config),
        eventGame: new EventGame(config),
        odds: new Odds(config),
        getVersion,
      };
      lastConfig = { ...config };

      return sdkInstance;
    } else {
      throwValidationMessage(validation);
    }
  }

  return sdkInstance;
}

const isNewInstanceRequired = (config: SDKConfigurationModel) => {
  return (
    !sdkInstance ||
    JSON.stringify(config) !== JSON.stringify(lastConfig) ||
    config.authProvider?.getIdToken() !== lastConfig.authProvider?.getIdToken() ||
    getJwtTokenFromCookie(config.cookieToken) !== getJwtTokenFromCookie(lastConfig?.cookieToken)
  );
};

const validateFansUnitedConfiguration = (config: SDKConfigurationModel) => {
  const configValidator = new ConfigurationValidator();
  const validation = configValidator.validate(config);

  if (
    validation.idSchema.validation === ValidationResponseEnum.MISSING_VALUE ||
    validation.idSchema.validation === ValidationResponseEnum.INVALID_VALUE
  ) {
    console.warn(validation.idSchema.message.text);
    config.idSchema = IdSchemaEnum.NATIVE;
  }

  if (
    validation.environment.validation ===
      ValidationResponseEnum.MISSING_VALUE ||
    validation.environment.validation === ValidationResponseEnum.INVALID_VALUE
  ) {
    console.warn(validation.environment.message.text);
    config.environment = EnvironmentEnum.PROD;
  }

  if (
    validation.lang.validation === ValidationResponseEnum.MISSING_VALUE ||
    validation.lang.validation === ValidationResponseEnum.INVALID_VALUE
  ) {
    console.warn(validation.lang.message.text);
    config.lang = LangEnum.EN;
  }

  if (
    validation.errorHandlingMode.validation ===
      ValidationResponseEnum.MISSING_VALUE ||
    validation.errorHandlingMode.validation ===
      ValidationResponseEnum.INVALID_VALUE
  ) {
    console.warn(validation.errorHandlingMode.message.text);
    config.errorHandlingMode = ErrorHandlingModeEnum.DEFAULT;
  }

  if (
    validation.authProvider.validation === ValidationResponseEnum.INVALID_VALUE
  ) {
    console.warn(validation.authProvider.message.text);
  }

  if (
    validation.oddClient.message.type === LogEnum.WARNING ||
    validation.oddClient.credentials.message.type === LogEnum.WARNING
  ) {
    console.warn(
      validation.oddClient.message.text ||
        validation.oddClient.credentials.message.text
    );
  }

  return validation;
};

const throwValidationMessage = (validation: ConfigValidationModel) => {
  const isValid = "isValid";

  Object.keys(validation).forEach((property: string) => {
    const fieldValidation = validation[property];
    if (property !== isValid && property !== ConfigFieldEnum.authProvider) {
      if (fieldValidation.message.type === LogEnum.ERROR) {
        throw new Error(fieldValidation.message.text);
      }
    } else if (
      property !== isValid &&
      property === ConfigFieldEnum.authProvider
    ) {
      if (fieldValidation?.message.type === LogEnum.ERROR) {
        throw new Error(fieldValidation.message.text);
      }

      Object.keys(new AuthProviderConfigModel()).forEach((key: string) => {
        if (fieldValidation[key].message.type === LogEnum.ERROR) {
          throw new Error(fieldValidation[key].message.text);
        }
      });
    }

    if (property !== isValid && property === ConfigFieldEnum.oddClient) {
      if (fieldValidation?.message.type === LogEnum.ERROR) {
        throw new Error(fieldValidation.message.text);
      }

      const validation = fieldValidation as OddClientValidationModel;

      if (validation.credentials.message.type === LogEnum.ERROR) {
        throw new Error(validation.credentials.message.text);
      }
    }
  });
};
